import './GeofenceEditor.scss';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Ajax } from '@shared/Ajax';
import { cs } from '@shared/utils/utils-classname';
import { Drawer } from '@shared/ui-native/Drawer';
import { transform as olTransform } from 'ol/proj';
import { Polygon as OlPolygon } from 'ol/geom';
import Feature from 'ol/Feature';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { Notify } from '@shared/ui-native/Notify';
import { CheckboxListDropdown, IFilterItem } from '@shared/ui-react/CheckboxListDropdown';
import { GeofenceDraw } from '@shared/trucks/GeofenceDraw';
import { GeofenceFeatureFactory, GeofenceType } from '@shared/trucks/GeofenceFeatureFactory';
import { MapWrapper } from '@openlayers/MapWrapper';
import { GeofencesStore } from '@pages/trucks/store/GeofencesStore';
import { MaterialsStore } from '@pages/trucks/store/MaterialsStore';
import { fitExtent } from '@openlayers/ol-utils';
import { StyleLike } from 'ol/style/Style';
import { Button } from '@shared/ui-react/Button';
import { DateSpan } from '@shared/ui-react/UtilComponents';

export const GeofenceEditor = {
    open(mapWrapper: MapWrapper, geofencesStore: GeofencesStore, materialsStore: MaterialsStore, geofenceUuid?: string, initialFeature?: Feature, onClose?: () => void) {
        Drawer.close();
        const el = Drawer.open(380);
        ReactUtils.safeRender(
            <GeofenceCreateUpdate
                geofenceUuid={geofenceUuid}
                initialFeature={initialFeature}
                geofencesStore={geofencesStore}
                materialsStore={materialsStore}
                mapWrapper={mapWrapper}
                onClose={onClose}
            />,
            el
        );
    },
    close() {
        Drawer.close();
    },
};

@observer
class GeofenceCreateUpdate extends React.Component<{
    geofenceUuid?: string;
    initialFeature?: Feature;
    mapWrapper?: MapWrapper;
    geofencesStore: GeofencesStore;
    materialsStore: MaterialsStore;
    onClose?: () => void;
}> {
    @observable id: number;
    @observable uuid: string;
    @observable title: string = '';
    @observable materialId: number = -1;
    @observable areaType: GeofenceType = GeofenceType.UNLOAD;
    @observable materials: IGeofenceMaterial[] = [];
    @observable storeMaterials = new Map<number, IFilterItem>();

    @observable attachedTruckIds: number[] = [];
    @observable availableVehicles: Partial<IVehicle>[] = [];
    @observable isLoadingVehicles: boolean = false;
    @observable isLoadingGeofence: boolean = false;
    // @observable unit: string = '';

    @observable isValidFeature: boolean = !!this.props.geofenceUuid;
    @observable isLoading: boolean = false;
    @observable startingVolumes: IMasshaulAreaVolume[] = [];

    geofence: IGeofence = null;

    draw: GeofenceDraw = null;
    initialFeatureStyle: StyleLike = null;

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    @action.bound
    applyGeofenceToState(geofence: IGeofence) {
        const firstMaterial = this.props.materialsStore.items[0] ? this.props.materialsStore.items[0].id : null;
        if (geofence) {
            this.geofence = geofence;
            this.id = geofence.id;
            this.uuid = geofence.uuid;
            this.title = geofence.title;
            this.materialId = geofence.materialId == -1 ? firstMaterial : geofence.materialId;
            // this.unit = geofence.unit;
            this.attachedTruckIds = geofence.attached_trucks.map(t => t.vehicle.id);
            this.areaType = geofence.type;
            this.materials = geofence.materials;
            this.startingVolumes = (geofence.startingVolumes ?? []).map(vol => ({
                uuid: vol.uuid,
                materialId: vol.material.id,
                volume: vol.volume,
                volumeUnit: vol.volumeUnit,
                created: vol.created,
            }));
        } else {
            this.materialId = firstMaterial;
        }
    }

    componentWillUnmount() {
        this.draw.cancel();
        if (this.props.initialFeature && !this.id) {
            this.props.initialFeature.setStyle(this.initialFeatureStyle);
        }
    }

    componentDidMount() {
        this.draw = new GeofenceDraw(this.props.mapWrapper);

        const errors = this.getPreconditionErrors();
        if (errors.length > 0) return;

        if (this.props.geofenceUuid) {
            this.fetchGeofence(this.props.geofenceUuid);
        } else {
            this.initCreateMode();
        }
    }

    fetchGeofence = async (uuid: string) => {
        this.isLoadingGeofence = true;
        try {
            const resp = await Ajax({
                url: 'project_masshaul_area.json',
                data: { areaUuid: uuid },
            });
            this.applyGeofenceToState(resp.area);
            this.initEditMode();
        } catch (e) {
            Notify.error();
            console.log('fetchGeofence error:', e);
        } finally {
            this.isLoadingGeofence = false;
        }
    };

    initEditMode() {
        this.isValidFeature = true;
        this.draw.startModify(this.geofence, this.props.initialFeature);

        this.props.materialsStore.items.forEach(mat => {
            this.storeMaterials.set(mat.id, {
                title: mat.material,
                value: mat.id,
                active: mat.id === this.materialId,
            });
        });

        this.fetchAvailableVehicles();
        this.checkAreaAllowedMaterials();
    }

    initCreateMode() {
        const firstMaterial = this.props.materialsStore.items[0] ? this.props.materialsStore.items[0].id : null;
        this.materialId = firstMaterial;

        if (this.props.initialFeature) {
            this.isValidFeature = true;
            let clone = this.props.initialFeature.clone();
            this.initialFeatureStyle = clone.getStyle();

            // geofence can only be simple solid feature, so if initialFeature has holes (when creating from real model), we need to discard holes and edit the clone
            let coords = (clone.getGeometry() as OlPolygon).getCoordinates()[0] as any[];
            if (coords.length > 1 && coords[0].length > 2) {
                clone.setGeometry(new OlPolygon([coords[0]]));
                this.draw.startModifyFeature(clone, true);
            } else {
                // we dont always use clone, so when they 'make geofence from area measurement' they will still get interactive measurement while drawing
                this.draw.startModifyFeature(this.props.initialFeature);
            }
        } else {
            // create new mode
            this.draw.startDrawing();
            this.draw.drawInteraction.on('drawend', () => {
                this.isValidFeature = true;
                this.setFeatureStyle(this.areaType, 'dashed');
            });
        }

        this.props.materialsStore.items.forEach(mat => {
            this.storeMaterials.set(mat.id, {
                title: mat.material,
                value: mat.id,
                active: mat.id === this.materialId,
            });
        });

        this.fetchAvailableVehicles();
        this.checkAreaAllowedMaterials();
    }

    render() {
        const errors = this.getPreconditionErrors();
        const isAdvanced = kuura.hasAdvancedTrucksEnabled();
        if (errors.length) {
            return (
                <div className='drawer-error'>
                    <div>{errors[0]}</div>
                    <button onClick={GeofenceEditor.close}>{__('general.close')}</button>
                </div>
            );
        }

        return (
            <div className={cs({ 'overlay-loading': this.props.geofencesStore.isDoingAsyncWork || this.isLoadingGeofence })} id='geofence-editor-wrapper'>
                <div className='form-group'>
                    <label>{__('trucks.geofence.name')} *</label>
                    <input
                        type='text'
                        maxLength={45}
                        value={this.title}
                        onChange={e => {
                            if (e.currentTarget.value.length >= 45) {
                                Notify.warn(__('general.validation.maximum_n_field_length', '45'));
                            }
                            this.title = e.currentTarget.value;
                        }}
                    />
                </div>

                {isAdvanced && (
                    <div className='form-group'>
                        <label>{__('trucks.geofence.material')}</label>
                        <select
                            value={this.materialId}
                            onChange={e => {
                                this.materialId = parseInt(e.currentTarget.value, 10);
                                this.storeMaterials.get(this.materialId).active = true;
                            }}
                        >
                            {this.props.materialsStore.items.map(m => (
                                <option value={m.id} key={m.id}>
                                    {m.material}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div className='form-group'>
                    <label>{__('trucks.geofence.materials')}</label>
                    <CheckboxListDropdown title={__('trucks.trips.table_material')} list={this.storeMaterials} />
                </div>

                {/*<div className='form-group'>
                    <label>{__('trucks.geofence.unit')}</label>
                    <input type='text' value={this.unit} onChange={e => this.unit = e.currentTarget.value}/>
                    <FormErrors error={this.unit?.trim().length > 120 ? { type: 'maxLength', maxLength: 120 } : null}/>
                </div>*/}

                <div className='form-group'>
                    <label>{__('trucks.geofence.type')}</label>

                    <div style={{ display: 'flex' }} className='btn-group geofence-type-selector'>
                        <button className={cs('ik-button-secondary', { active: this.areaType == GeofenceType.UNLOAD })} onClick={() => this.handleTypeChange(GeofenceType.UNLOAD)}>
                            <i className='iki iki-unload geofence-icon'></i>
                            <div>{__('trucks.trips.table_unload')}</div>
                        </button>

                        <button className={cs('ik-button-secondary', { active: this.areaType == GeofenceType.LOAD })} onClick={() => this.handleTypeChange(GeofenceType.LOAD)}>
                            <i className='iki iki-load geofence-icon'></i>
                            <div>{__('trucks.trips.table_load')}</div>
                        </button>

                        <button
                            className={cs('ik-button-secondary', { active: this.areaType == GeofenceType.HYBRID })}
                            onClick={() => this.handleTypeChange(GeofenceType.HYBRID)}
                            title={isAdvanced ? __('trucks.geofence.hybrid_availability') : ''}
                            disabled={isAdvanced}
                        >
                            <i className='iki iki-load-unload geofence-icon'></i>
                            <div>{__('trucks.geofence.hybrid')}</div>
                        </button>

                        {isAdvanced && (
                            <button className={cs({ active: this.areaType == GeofenceType.PARKING })} onClick={() => this.handleTypeChange(GeofenceType.PARKING)}>
                                <i className='iconf-parking geofence-icon'></i>
                                <div>{__('trucks.geofence.parking')}</div>
                            </button>
                        )}
                    </div>

                    {this.areaType == GeofenceType.HYBRID && <div style={{ color: '#cc9600', marginTop: 10, lineHeight: 1.1 }}>{__('trucks.geofence.hybrid_availability')}</div>}
                </div>

                <div className='form-group'>
                    <label>{__('trucks.geofence.draw_area')}</label>
                    <div className='geofence-start-draw'>
                        {!this.isValidFeature ? (
                            <div className='draw'>{__('trucks.geofence.draw_area_start_hint')}</div>
                        ) : (
                            <div className='modify'>{__('trucks.geofence.draw_area_modify_hint')}</div>
                        )}
                    </div>
                    <div style={{ float: 'right' }}>
                        {!this.isValidFeature || !this.id ? (
                            <label className='geofence-import btn ik-button-primary'>
                                {' '}
                                {__('general.import')}
                                <input
                                    type='file'
                                    accept='.kml, .xml'
                                    onChange={e => {
                                        GeofenceFeatureFactory.importAreaFromKml(e.target.files[0], this.draw).then(polyFeature => {
                                            this.isValidFeature = !!polyFeature;
                                            if (polyFeature) {
                                                fitExtent(this.props.mapWrapper.getInstance(), polyFeature.getGeometry().getExtent(), 250);
                                            }
                                        });
                                    }}
                                />
                            </label>
                        ) : (
                            <button
                                className='ik-button-primary'
                                disabled={!this.isFormValid() || this.isLoading || !this.id}
                                onClick={() => GeofenceFeatureFactory.exportAreaAsKml(this.id, this.title, this.areaType)}
                            >
                                {__('general.export')}
                            </button>
                        )}
                    </div>
                </div>

                {isAdvanced && this.areaType != GeofenceType.PARKING && (
                    <div className={cs('form-group', { 'overlay-loading': this.isLoadingVehicles })} style={{ overflow: 'hidden', flexShrink: 0 }}>
                        <label>{__('trucks.geofence.assign')}</label>
                        <div className='geofence-modify-area-hint'>{__('trucks.geofence.assign_hint')}</div>
                        {this.areaType == GeofenceType.HYBRID && <strong>{__('trucks.geofence.assign_manually')}</strong>}
                        {this.areaType != GeofenceType.HYBRID && this.availableVehicles.length == 0 && <strong>{__('trucks.geofence.no_free_vehicles')}</strong>}
                        {this.areaType != GeofenceType.HYBRID &&
                            this.availableVehicles.map((v, i) => (
                                <div key={i}>
                                    <label>
                                        <input
                                            type='checkbox'
                                            checked={this.attachedTruckIds.includes(v.id)}
                                            onChange={e => this.handleVehicleCheck(v.id, e.currentTarget.checked)}
                                        />{' '}
                                        {v.name}
                                    </label>
                                </div>
                            ))}
                    </div>
                )}

                <div className='form-group'>
                    <label>{__('trucks.starting_volumes')}</label>
                    <table className='starting-volumes-table'>
                        <thead>
                            <tr>
                                <th>{__('trucks.trips.table_material')}</th>
                                <th>{__('trucks.trips.table_volume')}</th>
                                <th>{__('trucks.geofence.unit')}</th>
                                <th></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.startingVolumes.map((vol, i) =>
                                vol.uuid ? (
                                    <tr key={i}>
                                        <td className='break-text'>{this.props.materialsStore.items.find(m => m.id == vol.materialId)?.material}</td>
                                        <td>{vol.volume}</td>
                                        <td>{vol.volumeUnit == 1 ? __('trucks.m3_loose') : vol.volumeUnit == 2 ? __('trucks.tons') : __('trucks.pieces')}</td>
                                        <td>
                                            <DateSpan timestamp={vol.created} />
                                        </td>
                                        <td>
                                            <Button onClick={() => this.removeStartingVolume(i)}>×</Button>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={i}>
                                        <td>
                                            <select value={vol.materialId} onChange={e => (vol.materialId = parseInt(e.currentTarget.value, 10))}>
                                                {this.props.materialsStore.items.map(m => (
                                                    <option value={m.id} key={m.id}>
                                                        {m.material}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <input type='number' step='any' value={vol.volume} onChange={e => (vol.volume = parseFloat(e.currentTarget.value) || 0)} />
                                        </td>
                                        <td>
                                            <select value={vol.volumeUnit} onChange={e => (vol.volumeUnit = parseInt(e.currentTarget.value) as any)}>
                                                <option value='1'>{__('trucks.m3_loose')}</option>
                                                <option value='2'>{__('trucks.tons')}</option>
                                                <option value='3'>{__('trucks.pieces')}</option>
                                            </select>
                                        </td>
                                        <td></td>
                                        <td>
                                            <Button onClick={() => this.removeStartingVolume(i)}>×</Button>
                                        </td>
                                    </tr>
                                )
                            )}
                        </tbody>
                    </table>
                    <button type='button' className='ik-button-secondary' style={{ marginTop: 8 }} onClick={this.addStartingVolume}>
                        <i className='iki iki-plus' /> {__('general.add')}
                    </button>
                </div>

                <footer>
                    <div style={{ float: 'right' }}>
                        <button
                            style={{ marginRight: '10px' }}
                            onClick={() => {
                                if (this.props.onClose) this.props.onClose();
                                GeofenceEditor.close();
                            }}
                            type='button'
                        >
                            {__('general.close')}
                        </button>
                        <button onClick={this.submit} ref='save' className='ik-button-primary' disabled={!this.isFormValid() || this.isLoading}>
                            {__(this.id ? 'general.save' : 'general.add')}
                        </button>
                    </div>
                </footer>
            </div>
        );
    }

    handleVehicleCheck(id, checked) {
        let ids = [...this.attachedTruckIds];

        if (checked) {
            ids.push(id);
        } else {
            ids = ids.filter(aid => aid !== id);
        }
        this.attachedTruckIds = ids;
    }

    handleTypeChange = areaType => {
        this.areaType = areaType;

        if (this.draw.currentFeature) {
            // when creating a new geofence, set dashed style on map
            this.setFeatureStyle(this.areaType, 'dashed');
        }
    };

    addStartingVolume = () => {
        const firstMaterialId = this.props.materialsStore.items[0]?.id ?? -1;
        this.startingVolumes.push({
            materialId: firstMaterialId,
            volume: 0,
            volumeUnit: 1,
        });
    };

    removeStartingVolume = (index: number) => {
        this.startingVolumes.splice(index, 1);
    };

    getMaterialIdsByValue = (materials: Map<number, IFilterItem>, isActive: boolean): number[] => {
        let activeKeys: number[] = [];
        for (let [key, value] of materials.entries()) {
            if (value.active === isActive) {
                activeKeys.push(key);
            }
        }
        return activeKeys;
    };

    submit = () => {
        this.isLoading = true;
        const geom = this.draw.currentFeature.getGeometry();
        const coords = (geom as OlPolygon).getCoordinates();

        let points = [];
        if (geom.getType() == 'Polygon') {
            points = coords[0];
        } else if (geom.getType() == 'MultiPolygon') {
            points = coords[0][0];
        }
        const geometry = points.map(p => olTransform([p[0], p[1]], 'EPSG:3857', 'EPSG:4326').reverse().join(' ')).join(',');

        if (geometry.includes('NaN')) {
            Notify.error('Error while trying to process model');
            return;
        }

        const data: any = {
            materialId: this.materialId,
            attached_trucks: this.attachedTruckIds.slice(),
            geometry,
            title: this.title,
            type: this.areaType,
            // unit: this.unit,
            materialIds: this.getMaterialIdsByValue(this.storeMaterials, true),
        };

        const volumesToSend: IMasshaulAreaVolumeInput[] = this.startingVolumes
            .filter(v => v.volume !== 0)
            .map(vol => ({
                uuid: vol.uuid,
                materialId: vol.materialId,
                volume: vol.volume,
                volumeUnit: vol.volumeUnit,
            }));

        let promise;
        if (this.id) {
            data.id = this.id;
            if (this.props.geofencesStore.checkIfGeofenceTitleAlreadyExists(data.title, data.id)) {
                Notify.warn(__('general.validation.name_already_exists'));
                this.isLoading = false;
            } else {
                promise = this.props.geofencesStore.update(data, volumesToSend);
            }
        } else {
            if (this.props.geofencesStore.checkIfGeofenceTitleAlreadyExists(data.title)) {
                Notify.warn(__('general.validation.name_already_exists'));
                this.isLoading = false;
            } else {
                promise = this.props.geofencesStore.create(data, volumesToSend);
            }
        }

        !!promise &&
            promise
                .then(this.props.geofencesStore.fetch)
                .done(() => {
                    if (this.props.onClose) this.props.onClose();
                    Notify.success();
                    GeofenceEditor.close();
                })
                .fail(Notify.error)
                .always(() => {
                    this.isLoading = false;
                });
    };

    setFeatureStyle(type: number, style: 'normal' | 'dashed') {
        (this.draw.currentFeature as Feature).setStyle(GeofenceFeatureFactory.styles[style][type]);
    }

    private getPreconditionErrors() {
        const errors: string[] = [];
        if (this.props.materialsStore.items.length === 0) {
            errors.push('To create a geofence, first create at least one material');
        }
        return errors;
    }

    private isFormValid() {
        return this.title.trim().length > 0 && this.isValidFeature /*&& (this.unit || '').trim().length <= 120*/;
    }

    private checkAreaAllowedMaterials() {
        if (!!this.materials.length) {
            const materialIds = this.materials.map(t => t.id) || [];

            this.storeMaterials.forEach((val, key) => {
                this.storeMaterials.get(key).active = materialIds.indexOf(key) !== -1;
            });

            this.storeMaterials.get(this.materialId).active = true;
        }
    }

    private fetchAvailableVehicles() {
        if (this.areaType !== GeofenceType.LOAD && this.areaType !== GeofenceType.UNLOAD) return;

        this.isLoadingVehicles = true;

        if (this.id) {
            return Ajax({
                url: 'masshaul_area_trucks.json',
                data: {
                    massHaulAreaId: this.id,
                },
            })
                .done(resp => {
                    const trucks = [];

                    resp.attached_trucks.forEach(link => {
                        trucks.push(link.vehicle);
                    });

                    resp.unattached_trucks.forEach(vehicle => {
                        trucks.push(vehicle);
                    });

                    this.availableVehicles = trucks.sort((a, b) => a.id - b.id);
                })
                .always(() => (this.isLoadingVehicles = false));
        } else {
            const areaType = {
                [GeofenceType.UNLOAD]: 'trucks_with_no_unload_area.json',
                [GeofenceType.LOAD]: 'trucks_with_no_load_area.json',
            };

            if (areaType[this.areaType]) {
                return Ajax({
                    url: areaType[this.areaType],
                })
                    .done(resp => {
                        this.availableVehicles = resp.trucks;
                    })
                    .always(() => (this.isLoadingVehicles = false));
            }
        }
    }
}
